import { Chip, alpha } from '@mui/material'
import {
  DataObject as JsonIcon,
  Code as XmlIcon,
  TextFields as TextIcon,
  Description as EdiIcon,
  AttachFile as BinaryIcon,
  Web as FormIcon,
  CloudUpload as MultipartIcon,
  Description as DefaultIcon,
} from '@mui/icons-material'

export interface ContentTypeTagProps {
  contentType: string
  size?: 'small' | 'default' | 'large'
}

interface ContentTypeConfig {
  label: string
  icon: React.ComponentType<any>
  color: string
  backgroundColor: string
  borderColor: string
}

const CONTENT_TYPES = [
  'application/json',
  'application/xml',
  'text/xml',
  'text/plain',
  'application/EDI-X12',
  'application/octet-stream',
  'application/x-www-form-urlencoded',
  'multipart/form-data',
] as const

type ContentType = typeof CONTENT_TYPES[number]

const CONTENT_TYPE_MAPPING: Record<ContentType, ContentTypeConfig> = {
  'application/json': {
    label: 'JSON',
    icon: JsonIcon,
    color: '#1976d2',
    backgroundColor: alpha('#1976d2', 0.08),
    borderColor: alpha('#1976d2', 0.3),
  },
  'application/xml': {
    label: 'XML',
    icon: XmlIcon,
    color: '#388e3c',
    backgroundColor: alpha('#388e3c', 0.08),
    borderColor: alpha('#388e3c', 0.3),
  },
  'text/xml': {
    label: 'XML',
    icon: XmlIcon,
    color: '#388e3c',
    backgroundColor: alpha('#388e3c', 0.08),
    borderColor: alpha('#388e3c', 0.3),
  },
  'text/plain': {
    label: 'Plain Text',
    icon: TextIcon,
    color: '#f57c00',
    backgroundColor: alpha('#f57c00', 0.08),
    borderColor: alpha('#f57c00', 0.3),
  },
  'application/EDI-X12': {
    label: 'EDI X12',
    icon: EdiIcon,
    color: '#7b1fa2',
    backgroundColor: alpha('#7b1fa2', 0.08),
    borderColor: alpha('#7b1fa2', 0.3),
  },
  'application/octet-stream': {
    label: 'Binary',
    icon: BinaryIcon,
    color: '#d32f2f',
    backgroundColor: alpha('#d32f2f', 0.08),
    borderColor: alpha('#d32f2f', 0.3),
  },
  'application/x-www-form-urlencoded': {
    label: 'Form URL Encoded',
    icon: FormIcon,
    color: '#0288d1',
    backgroundColor: alpha('#0288d1', 0.08),
    borderColor: alpha('#0288d1', 0.3),
  },
  'multipart/form-data': {
    label: 'Multipart Form Data',
    icon: MultipartIcon,
    color: '#5d4037',
    backgroundColor: alpha('#5d4037', 0.08),
    borderColor: alpha('#5d4037', 0.3),
  },
}

function getMappedContentType(contentType: string): ContentTypeConfig {
  // Try to find a mapping key that matches the contentType using regex
  const mappingKey = Object.keys(CONTENT_TYPE_MAPPING).find(key => {
    const regex = new RegExp(`^${key.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}`, 'i')
    return regex.test(contentType)
  })

  if (mappingKey) {
    return CONTENT_TYPE_MAPPING[mappingKey]
  }

  // Default configuration for unmapped content types
  return {
    label: contentType,
    icon: DefaultIcon,
    color: '#757575',
    backgroundColor: alpha('#757575', 0.08),
    borderColor: alpha('#757575', 0.3),
  }
}

export default function ContentTypeTag(props: ContentTypeTagProps) {
  const { contentType, size = 'default' } = props

  // Handle undefined/null contentType
  if (!contentType) {
    return null
  }

  const { label, icon: IconComponent, color, backgroundColor, borderColor } = getMappedContentType(
    contentType
  )

  // Map custom sizes to MUI Chip sizes
  const chipSize = size === 'large' ? 'medium' : 'small'

  // Icon size based on chip size
  const iconSize = size === 'small' ? 12 : size === 'default' ? 14 : 16

  return (
    <Chip
      icon={<IconComponent sx={{ fontSize: iconSize }} />}
      label={label?.toUpperCase() || 'UNKNOWN'}
      size={chipSize}
      variant="outlined"
      sx={{
        fontSize: size === 'small' ? '0.75rem' : size === 'default' ? '0.875rem' : '1rem',
        height: size === 'small' ? 20 : size === 'default' ? 24 : 28,
        color: color,
        backgroundColor: backgroundColor,
        borderColor: borderColor,
        fontWeight: 'bold',
        borderRadius: '12px',
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          backgroundColor: alpha(color, 0.12),
          borderColor: alpha(color, 0.5),
          transform: 'translateY(-1px)',
          boxShadow: `0 2px 8px ${alpha(color, 0.2)}`,
        },
        '& .MuiChip-icon': {
          fontSize: iconSize,
          color: color,
        },
        '& .MuiChip-label': {
          color: color,
          fontWeight: 'bold',
        },
      }}
    />
  )
}
